import { useState } from "react";
import { useTestShowcase, fmt } from "@/hooks/useAdmin";
import { AdminHeading, Badge } from "@/components/admin/AdminLayout";
import { ChevronDown, ChevronUp, CheckCircle2, XCircle, MinusCircle, Ban } from "lucide-react";

const stageTone = {
  completed: "sage", "completed (private)": "sage", waitlisted: "champagne",
  declined: "muted", withdrawn: "red",
};
const statusIcon = {
  pass: <CheckCircle2 size={14} className="text-forest" />,
  fail: <XCircle size={14} className="text-destructive" />,
  blocked: <Ban size={14} className="text-champagne" />,
  na: <MinusCircle size={14} className="text-charcoal/40" />,
};

function Tally({ j }) {
  return (
    <div className="flex flex-wrap gap-2 text-[10px] uppercase tracking-[0.16em]" data-testid={`tally-${j.persona_id}`}>
      <span className="text-forest">{j.pass} pass</span>
      <span className={j.fail ? "text-destructive" : "text-charcoal/40"}>{j.fail} fail</span>
      <span className="text-champagne">{j.blocked} blocked</span>
      <span className="text-charcoal/40">{j.na} n/a</span>
    </div>
  );
}

function JourneyCard({ j }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-forest/15 bg-cream" data-testid={`journey-card-${j.persona_id}`}>
      <div className="p-6">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="font-serif text-2xl text-forest font-light">{j.name}</p>
            <p className="text-xs text-charcoal/55 font-light mt-0.5">{j.city} · {j.age_band} · {j.skin_profile}</p>
          </div>
          <Badge tone={stageTone[j.current_stage] || "forest"}>{j.current_stage}</Badge>
        </div>
        <dl className="mt-4 space-y-1.5 text-sm">
          <Row k="Scenario" v={j.scenario} />
          <Row k="Product interest" v={j.product_interest} />
          <Row k="Application" v={j.application ? `${j.application.status}` : "—"} />
          <Row k="Trial" v={j.trial ? j.trial.status : (j.shipment_status || "—")} />
          <Row k="Consent" v={`marketing ${j.marketing_consent ? "yes" : "no"} · story ${j.story_consent ? "yes" : "no"}`} />
          <Row k="Story" v={j.story ? j.story.status : "none"} />
          {j.support_case && <Row k="Support case" v={`${j.support_case.type} · ${j.support_case.status}`} />}
        </dl>
        {j.unique_feedback && (
          <p className="mt-4 border-l-2 border-champagne pl-3 text-sm text-charcoal/70 italic font-light" data-testid={`feedback-${j.persona_id}`}>
            “{j.unique_feedback}”
          </p>
        )}
        <div className="mt-4 flex items-center justify-between">
          <Tally j={j} />
          <button onClick={() => setOpen((o) => !o)} data-testid={`toggle-${j.persona_id}`}
            className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-forest hover:text-olive">
            {open ? <>Hide detail <ChevronUp size={13} /></> : <>Timeline & checks <ChevronDown size={13} /></>}
          </button>
        </div>
      </div>
      {open && (
        <div className="border-t border-forest/10 bg-ivory px-6 py-5 space-y-6">
          <div>
            <p className="eyebrow mb-3">Timeline</p>
            <ol className="space-y-2">
              {j.timeline?.map((t, i) => (
                <li key={i} className="flex gap-3 text-sm">
                  <span className="text-charcoal/40 whitespace-nowrap text-xs pt-0.5">{fmt(t.at)}</span>
                  <span className="text-[10px] uppercase tracking-[0.16em] text-champagne pt-1 w-16 shrink-0">{t.actor}</span>
                  <span className="text-charcoal/75 font-light">{t.action} <span className="text-charcoal/45">— {t.result}</span></span>
                </li>
              ))}
            </ol>
          </div>
          <div>
            <p className="eyebrow mb-3">Checks</p>
            <div className="space-y-1.5">
              {j.checks?.map((c, i) => (
                <div key={i} className="flex gap-2 text-sm items-start" data-testid={`check-${j.persona_id}-${i}`}>
                  <span className="pt-0.5">{statusIcon[c.status]}</span>
                  <div>
                    <p className="text-charcoal/80 font-light">{c.step}</p>
                    <p className="text-xs text-charcoal/45">exp: {c.expected} · got: {c.actual}{c.defect ? ` · ${c.defect}` : ""}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const Row = ({ k, v }) => (
  <div className="flex justify-between gap-3"><dt className="text-charcoal/50 font-light">{k}</dt><dd className="text-forest text-right">{v}</dd></div>
);

export default function AdminTestShowcase() {
  const { data, isLoading } = useTestShowcase();
  if (isLoading) return <p className="text-[11px] uppercase tracking-[0.24em] text-charcoal/50">Loading…</p>;
  const run = data?.run;
  const journeys = data?.journeys || [];

  if (!run) return (
    <div data-testid="admin-test-showcase">
      <AdminHeading eyebrow="QA" title="Test Journey Showcase" />
      <div className="border border-forest/15 bg-cream p-8 text-charcoal/60 font-light">
        No test run found yet. Run <code className="text-forest">python /app/scripts/test_run_journeys.py</code> to populate the five customer journeys.
      </div>
    </div>
  );

  return (
    <div data-testid="admin-test-showcase">
      <AdminHeading eyebrow="QA" title="Test Journey Showcase">
        <div className="text-right">
          <Badge tone="red">DEMO / TEST DATA — NOT REAL CUSTOMERS</Badge>
          <p className="mt-2 text-xs text-charcoal/45 font-light">Run {run.test_run_id} · {fmt(run.created_at)}</p>
        </div>
      </AdminHeading>

      <div className="mb-8 grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
        {["pass", "fail", "blocked", "na"].map((k) => (
          <div key={k} className="border border-forest/12 bg-cream p-4" data-testid={`total-${k}`}>
            <p className="font-serif text-4xl text-forest font-light">{run.totals?.[k] ?? 0}</p>
            <p className="text-[10px] uppercase tracking-[0.2em] text-charcoal/50 mt-1">{k}</p>
          </div>
        ))}
      </div>

      {/* comparison table */}
      <div className="mb-10 overflow-x-auto border border-forest/12" data-testid="comparison-table">
        <table className="w-full text-sm">
          <thead className="bg-forest text-ivory text-[10px] uppercase tracking-[0.16em]">
            <tr>{["Customer", "City", "Scenario", "Interest", "Outcome", "Checks"].map((h) => (
              <th key={h} className="text-left font-normal px-4 py-3">{h}</th>))}</tr>
          </thead>
          <tbody>
            {run.comparison?.map((c, i) => (
              <tr key={i} className="border-t border-forest/10 bg-cream">
                <td className="px-4 py-3 text-forest">{c.name}</td>
                <td className="px-4 py-3 text-charcoal/60 font-light">{c.city}</td>
                <td className="px-4 py-3 text-charcoal/70 font-light">{c.scenario}</td>
                <td className="px-4 py-3 text-charcoal/60 font-light">{c.product_interest}</td>
                <td className="px-4 py-3"><Badge tone={stageTone[c.outcome] || "forest"}>{c.outcome}</Badge></td>
                <td className="px-4 py-3 text-xs text-charcoal/55 font-light">{c.pass}✓ {c.fail}✗ {c.blocked}⚠ {c.na}–</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* per-customer cards */}
      <div className="grid lg:grid-cols-2 gap-5">
        {journeys.map((j) => <JourneyCard key={j.persona_id} j={j} />)}
      </div>

      {/* authorization matrix */}
      <div className="mt-10">
        <p className="eyebrow mb-3">Authorization & privacy matrix</p>
        <div className="border border-forest/12 divide-y divide-forest/10" data-testid="authz-matrix">
          {run.authorization_checks?.map((a, i) => (
            <div key={i} className="flex items-start gap-2 px-4 py-2.5 bg-cream text-sm">
              <span className="pt-0.5">{statusIcon[a.status]}</span>
              <div><p className="text-charcoal/80 font-light">{a.check}</p>
                <p className="text-xs text-charcoal/45">{a.expected} · {a.actual}</p></div>
            </div>
          ))}
        </div>
      </div>

      {/* gaps + seeded */}
      <div className="mt-10 grid md:grid-cols-2 gap-6">
        <div>
          <p className="eyebrow mb-3">Known gaps</p>
          <ul className="space-y-3">
            {run.gaps?.map((g) => (
              <li key={g.id} className="border border-forest/12 bg-cream p-4 text-sm">
                <p className="text-forest">{g.id} · {g.title}</p>
                <p className="text-charcoal/60 font-light mt-1">{g.detail}</p>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <p className="eyebrow mb-3">Seeded / simulated</p>
          <ul className="space-y-2 text-sm text-charcoal/65 font-light list-disc pl-5">
            {run.seeded_or_simulated?.map((s, i) => <li key={i}>{s}</li>)}
          </ul>
        </div>
      </div>
    </div>
  );
}
